import InputStyles from 'almanac-ui/TextInputField/Input.module.scss'
import { InputSize } from 'almanac-ui/TextInputField/Types'
import cx from 'classnames'
import { ProseInput } from 'common/ProseInput/ProseInput'
import { ProseInputProps } from 'common/ProseInput/Types'
import { PillsPlugin } from 'common/ProseInput/plugins/PillsPlugin'
import { useMemo, useRef, useState } from 'react'
import Styles from './PillsInput.module.scss'
import { ProseState } from 'common/ProseInput/ProseState'
import { ProseStateProvider } from 'common/ProseInput/ProseStateContext'
import { castArray } from 'lodash'

export interface PillsInputProps {
  pills: string[] | undefined
  onChange?: (pills: string[] | undefined) => void
  placeholder?: string
  footer?: (focused: boolean) => React.ReactNode
  splitOn?: string | string[]
  'aria-label'?: string
  className?: string
  isValid?: boolean
  size?: InputSize
  inputRef?: any
  autoFocus?: boolean
  plugins?: ProseInputProps['plugins']
  validatePill?: (pill: string) => boolean
  growOnFocus?: boolean
}

export const PillsInput: React.FC<React.PropsWithChildren<PillsInputProps>> = ({
  pills,
  onChange,
  placeholder = 'Use tab or comma to separate keywords',
  splitOn = ',',
  className,
  footer,
  inputRef,
  isValid = true,
  size = 'md',
  autoFocus,
  plugins,
  validatePill = (pill: string) => true,
  growOnFocus = true,
}) => {
  const [focused, setFocused] = useState(autoFocus ?? false)
  const [proseState] = useState(() => new ProseState())
  const wrapRef = useRef<HTMLDivElement>(null)
  const [localPills, setLocalPills] = useState<string[] | undefined>(pills)

  const memoPlugins = useMemo(
    () => ({
      mentions: plugins?.mentions,
      other: [...(plugins?.other || []), PillsPlugin({ splitOn: castArray(splitOn), validatePill })],
    }),
    [plugins?.mentions, plugins?.other, splitOn, validatePill],
  )

  return (
    <ProseStateProvider state={proseState}>
      <div
        className={cx(className, InputStyles.input, InputStyles[size], Styles.wrap, {
          [InputStyles.invalid]: !isValid,
          [InputStyles.focused]: focused,
          [Styles.focused]: focused,
          [Styles.growOnFocus]: growOnFocus,
        })}
        ref={wrapRef}
      >
        <ProseInput
          ref={inputRef}
          className={cx(Styles.input, InputStyles[size], {
            [Styles.empty]: (localPills?.length || 0) === 0,
          })}
          initialHtmlString={pills?.join(splitOn[0]) ?? ''}
          plugins={memoPlugins}
          handleKeyDown={(view, event) => {
            // Swallow newlines
            return event.key === 'Enter'
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            if (!e.relatedTarget || !wrapRef.current?.contains(e.relatedTarget as Node)) {
              setFocused(false)
            }
          }}
          onChange={(_htmlString, _plainText, _mentions, pills) => {
            setLocalPills(pills)
            onChange?.(pills)
          }}
        />
        {footer?.(focused)}
      </div>
    </ProseStateProvider>
  )
}
